import React from 'react';
import { Box, Container, Grid, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import VerifiedIcon from '@mui/icons-material/Verified';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SecurityIcon from '@mui/icons-material/Security';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import HandymanIcon from '@mui/icons-material/Handyman';

const FeaturePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  borderRadius: theme.shape.borderRadius,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
    '& .icon-wrapper': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: '50%',
  width: 80,
  height: 80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  transition: 'background-color 0.3s ease, color 0.3s ease',
  '& svg': {
    fontSize: 40,
    color: theme.palette.primary.main,
    transition: 'color 0.3s ease',
  },
}));

const features = [
  {
    id: 1,
    title: 'Years of Experience',
    description: 'Our team brings extensive industry knowledge and expertise to every job, ensuring professional results.',
    icon: <VerifiedIcon />,
  },
  {
    id: 2,
    title: 'Transparent Pricing',
    description: 'No hidden fees or surprises. We provide clear, upfront pricing for all our services.',
    icon: <AttachMoneyIcon />,
  },
  {
    id: 3,
    title: 'Fully Insured',
    description: 'Rest easy knowing that our services are fully insured for your complete protection and peace of mind.',
    icon: <SecurityIcon />,
  },
  {
    id: 4,
    title: 'Customer Focused',
    description: 'We prioritize your satisfaction by delivering personalized service that exceeds expectations.',
    icon: <PeopleIcon />,
  },
  {
    id: 5,
    title: 'Independently Owned',
    description: 'As an independent local business, we provide personalized attention and care to every customer.',
    icon: <BusinessIcon />,
  },
  {
    id: 6,
    title: 'Stress-Free Assembly',
    description: 'We offer complete furniture assembly services so you can enjoy your purchases right away.',
    icon: <HandymanIcon />,
  },
];

const WhyChooseUs: React.FC = () => {
  return (
    <Box sx={{ py: 8, backgroundColor: 'grey.50' }}>
      <Container>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h2"
            component="h2"
            fontWeight={700}
            gutterBottom
          >
            Why Choose Us
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: '800px', mx: 'auto' }}
          >
            JBR Delivery LLC is committed to providing exceptional service with these key benefits that set us apart from the competition.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature) => (
            <Grid item xs={12} sm={6} md={4} key={feature.id}>
              <FeaturePaper elevation={2}>
                <IconWrapper className="icon-wrapper">
                  {feature.icon}
                </IconWrapper>
                <Typography variant="h5" component="h3" gutterBottom fontWeight={600}>
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {feature.description}
                </Typography>
              </FeaturePaper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default WhyChooseUs;
